import { Vector2Like } from "type_aliases";
import VisibilityManager from "./visibility_manager";
import { getAllElementIds, getAllForgeInstanceTreeIds, setElementOff } from "../utilities/forge_utilities";
import ColorLib from "tinycolor2";
import globalStyles from "../../style/global_styles";
import ForgeCameraControlsExtension from "./extensions/forge_camera_controls_extension";
import { patchFragmentListGetMaterial } from "./patch_fragment_list";
import { hasParkedViewer } from "./parked_viewer";
import { isBigBimViewerPerformanceOn } from "../utilities/big_bim_viewer_performance";

interface AutodeskNavigation extends Autodesk.Viewing.Navigation {
  setWorldUpVector(upVector: THREE.Vector3, flag: boolean): any;

  orientCameraUp(force?: boolean): void;
}

export interface AutodeskViewer extends Autodesk.Viewing.GuiViewer3D {
  navigation: AutodeskNavigation;
}

export type SelectionChangedEvent = {
  selections: {
    fragIdsArray: number[]
    dbIdArray: number[],
    nodeArray: number[],
    model: Autodesk.Viewing.Model
  }[],
  target: Autodesk.Viewing.GuiViewer3D
};


export default class AutodeskForgeAdaptor {
  private visibleIds: Set<number>;
  /** The ids the model's visibility was last set from; null until it first is. */
  private appliedVisibleIds: Set<number> = null;

  constructor(viewer?: AutodeskViewer) {
    this.autodeskViewer = viewer;
    this.visibilityManager = new VisibilityManager(viewer);
    this.visibleIds = new Set();
  }

  flagForRerenderWithMovedObjects() {
    this.autodeskViewer.impl.sceneUpdated(true);
  }

  flagForRerenderWithNoObjectsMoved() {
    this.autodeskViewer.impl.sceneUpdated(false);
  }

  flagOverlayForRerender() {
    this.autodeskViewer.impl.invalidate(true, true, true);
  }

  loadExtension(extensionName, options = {}) {
    return this.autodeskViewer.loadExtension(extensionName, options);
  }

  getVisibleIds(): Set<number> {
    return this.visibleIds;
  }

  isVisible(id: number): boolean {
    return this.getVisibleIds().has(id);
  }

  makeVisible(filteredIds: Set<number>) {
    this.visibleIds = filteredIds;
    if (!isBigBimViewerPerformanceOn()) {
      this.appliedVisibleIds = null;
      const visibilityManager = this.visibilityManager;
      this.getAllDbIds().forEach(dbId => {
        if (filteredIds.has(dbId)) {
          visibilityManager.showMesh(dbId, this.getModel());
        } else {
          visibilityManager.hideMesh(dbId, this.getModel());
        }
      });
      return;
    }
    const previous = this.appliedVisibleIds;
    this.appliedVisibleIds = filteredIds;
    if (filteredIds === previous) {
      return;
    }

    const model = this.getModel();
    if (previous == null) {
      this.getAllDbIds().forEach(dbId => setElementOff(model, dbId, !filteredIds.has(dbId)));
    } else {
      // Only the elements whose visibility changed: a walk over every element of a big model costs seconds.
      previous.forEach(dbId => {
        if (!filteredIds.has(dbId)) {
          setElementOff(model, dbId, true);
        }
      });
      filteredIds.forEach(dbId => {
        if (!previous.has(dbId)) {
          setElementOff(model, dbId, false);
        }
      });
    }
    if (!model.getInstanceTree()) {
      // Turning fragments off directly (no instance tree) doesn't tell the scene; once is enough.
      this.autodeskViewer.impl.sceneUpdated(true);
    }
  }

  getAllDbIds(): number[] {
    if (!isBigBimViewerPerformanceOn()) {
      return getAllForgeInstanceTreeIds(this.getModel().getInstanceTree());
    }
    return getAllElementIds(this.getModel());
  }

  getExtension(extensionName) {
    return this.autodeskViewer.getExtension(extensionName);
  }

  addSelectionChangedEventListener(callback: (event: SelectionChangedEvent) => void) {
    this.autodeskViewer.addEventListener(Autodesk.Viewing.AGGREGATE_SELECTION_CHANGED_EVENT, callback);
  }

  removeSelectionChangedEventListener(callback: (event: SelectionChangedEvent) => void) {
    this.autodeskViewer.removeEventListener(Autodesk.Viewing.AGGREGATE_SELECTION_CHANGED_EVENT, callback);
  }

  fitToView(dbIds: number[]) {
    return this.autodeskViewer.fitToView(dbIds, this.getModel());
  }

  waitForLoadDone(include) {
    // @ts-ignore
    return this.autodeskViewer.waitForLoadDone(include);
  }

  setView(position: THREE.Vector3, target: THREE.Vector3) {
    this.autodeskViewer.navigation.setView(position, target);
  }

  getModel() {
    return this.autodeskViewer.model;
  }

  getCamera(): THREE.Camera {
    return this.autodeskViewer && this.autodeskViewer.navigation ? this.autodeskViewer.navigation.getCamera() : null;
  }

  shutdown() {
    // unloadModel() is intentionally omitted here: finish() unloads all models
    // as part of its teardown, and calling unloadModel() first triggers an
    // async callback inside the SDK that races with finish() zeroing out the
    // loading manager, producing a spurious _reserveLoadingFile null-read error.
    this.autodeskViewer.finish();
    // Skip global library teardown if a newer viewer has already taken over,
    // which can happen when this shutdown is deferred via setTimeout and the
    // user navigated to another viewer page before it fired, or while the
    // Explorer's viewer is parked (eg: the summary page's viewer closing).
    if ((!window.Avvir?.ForgeViewer || window.Avvir.ForgeViewer === this) && !hasParkedViewer()) {
      Autodesk.Viewing.shutdown();
    }
  }

  resize = () => {
    this.autodeskViewer.resize();
  }

  showCameraHelper() {
    (this.getExtension("Avvir.ForgeCameraControls") as ForgeCameraControlsExtension).tool.cameraControls.showHelper();
  }

  hideCameraHelper() {
    (this.getExtension("Avvir.ForgeCameraControls") as ForgeCameraControlsExtension).tool.cameraControls.hideHelper();
  }

  disableReflectionMap = () => {
    this.autodeskViewer?.impl.matman().setReflectionMap(null);
  };

  setLightPreset = (preset: number) => {
    this.autodeskViewer.setLightPreset(preset);
  };

  autodeskViewer: AutodeskViewer;
  visibilityManager: VisibilityManager;
}


export const initializeViewer = (url: string, floorOffset: Vector2Like, getGcpToken: (projectId: string) => Promise<{ accessToken: string, secondsUntilTokenExpires: number }>, containerElement: HTMLElement, onInitialized, projectId: string) => {
  const options: Autodesk.Viewing.InitializerOptions = {
    env: "AutodeskProduction",
    document: url,
    getAccessToken: (onTokenReady) => {
      getGcpToken(projectId).then((response) => {
        onTokenReady(response.accessToken, response.secondsUntilTokenExpires);
      });
    }
  };
  Autodesk.Viewing.Initializer(options, () => {
    // Harden the render loop against consolidated/non-resident fragments before any model loads.
    patchFragmentListGetMaterial();
    const autodeskViewer = new Autodesk.Viewing.GuiViewer3D(containerElement) as AutodeskViewer;
    const adaptor = new AutodeskForgeAdaptor(autodeskViewer);
    let loadOptions = {
      applyScaling: "m",
      globalOffset: { x: floorOffset?.x || 0, y: floorOffset?.y || 0, z: 0 }
    };
    // The types in @types/forge-viewer and the documentation don't match on this...
    // @ts-ignore
    const startedCode = autodeskViewer.start(url, loadOptions, () => onInitialized(adaptor), null, { useIdBufferSelection: true });
    autodeskViewer.setSelectionColor(new THREE.Color(new ColorLib(globalStyles.selectionBlue).toHexString()), Autodesk.Viewing.SelectionMode.MIXED);
    autodeskViewer.setReverseZoomDirection(true);
    if (startedCode > 0) {
      console.error("Failed to create a Viewer: WebGL not supported.");
    }
  });
};

